<template>
    <div>
        <MainModal
            :show-modal="showModal" 
            size="md" 
            title="Manage time tracking" 
            :footer="false"
            header-text-transform="none"
            @close="closeModal"
        >
            <template #body>
                <div class="wrap">
                    <NonLinkedTabs :list="tabHeaderList" type="pill" position="end" @select="v => selectedTab = v.name">
                        <template #nonLinkedTab="tab">
                            <NonLinkedTab :is-active="tab.name === 'Scheduled trackers'">
                                <div class="invoice__manage__tax">
                                    <template v-if="!trackers.length">
                                        <EmptyPage 
                                            state="empty_custom" 
                                            title="No scheduled trackers yet." 
                                            icon-name="time" 
                                            subtitle="Schedule a time tracker to see it show up here"
                                        />
                                    </template>

                                    <template v-else>
                                        <CustomTable
                                            :columns="tabHeaderList" :is-action-btns="false"
                                            :data="trackers" :is-checkbox="false" row-type="link"
                                        >
                                            <template #rows="data">
                                                <td>
                                                    {{ $format_date(data.startDate) }}
                                                </td>
                                                <td>
                                                    {{ $format_date(data.createdAt) }}
                                                </td>
                                            </template>
                                        </CustomTable>
                                    </template>
                                </div>
                            </NonLinkedTab>
                            
                            <NonLinkedTab :is-active="tab.name === 'Tracking logs'">
                                <template v-if="fetchLogsLoading">
                                    <PageSpinner />
                                </template>

                                <template v-else-if="fetchLogsLoadingErr">
                                    <EmptyPage state="error" @action="handleFetchLogs" />
                                </template>

                                <template v-else>
                                    <div>
                                        <EmptyPage v-if="!activityLogs.length" state="empty" type="log" icon-name="grid" />
                        
                                        <template v-else>
                                            <CustomTable
                                                :columns="timeSheetHeaders" :is-action-btns="false"
                                                :data="activityLogs" :is-checkbox="false" row-type="link"
                                            >
                                                <template #rows="data">
                                                    <td>
                                                        {{ data.startDate }} - {{ data.startTime }}
                                                    </td>
                                                    <td>
                                                        {{ data.endDate }} - {{ data.endTime }}
                                                    </td>
                                                    <td>
                                                        {{ data.duration }}
                                                    </td>
                                                </template>
                                            </CustomTable>

                                            <div class="flex justify-content-between mb--20 mt--20 align-items-center">
                                                <p class="text--sm text-gray-500">
                                                    Showing the latest 5
                                                </p>
                                                <div>
                                                    <BaseButton
                                                        v-if="activityLogs.length < 50" 
                                                        variant-style="secondary"
                                                        label="Export logs" 
                                                        variant="ghost" 
                                                        prefix-icon
                                                        medium
                                                        class="p-0"
                                                        icon-name="export"
                                                        :disabled="exportLogsLoading"
                                                        @click="exportLogs()"
                                                    />
                                                    <DropDownButton 
                                                        v-else
                                                        id="projectLogs"
                                                        variant="ghost"
                                                        prefix-icon
                                                        bold
                                                        icon-name="export"
                                                        variant-style="primary" 
                                                        label="Export logs"
                                                        size="sm"
                                                        :disabled="exportLogsLoading"
                                                    >
                                                        <template #dropdown-content>
                                                            <li 
                                                                v-for="num in [50, 100, 200]" 
                                                                :key="num"
                                                                class="dropdown-item dropdown--item flex align-items-center" 
                                                                @click="exportLogs(num)"
                                                            >
                                                                <IconSvg name="grid-small" width="16px" fill="#64748b" />
                                                                <span class="text--sm text-gray-800 ml--5">{{ num }} items</span>
                                                            </li>
                                                        </template>
                                                    </DropDownButton>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </template>
                            </NonLinkedTab>
                        </template>
                    </NonLinkedTabs>
                </div>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton
                        label="Close"
                        size="sm" 
                        @submit="closeModal"
                    />
                </div>
            </template>
        </MainModal>

        <UpgradeAccountModal 
            :show-modal="showUpgradeModalAlt" 
            type="logs"
            :custom-text="showUpgradeModalAlt"
            @cancel="showUpgradeModalAlt = false"
        >
            <template #text>
                <div>
                    <p class="text--sm text-gray-400">
                        This feature is only available to users on a <strong>Plus</strong> plan.
                    </p>
                    <p class="text--sm text-gray-400 mt--10">
                        To unlock this feature, go to account settings and upgrade your account.
                    </p> 
                </div>
            </template>
        </UpgradeAccountModal>
    </div>
</template>
  
<script>
import BaseButton from "../buttons/BaseButton.vue";
import DropDownButton from "../buttons/DropDownButton.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import MainModal from "./MainModal.vue";
import CustomTable from "../tables/index.vue";
import { timeSheetHeaders, trackerHeaders} from "../../../utils/dummy";
import UpgradeAccountModal from "../modals/UpgradeAccount.vue";
import PageSpinner from "../loaders/PageSpinner.vue";
import EmptyPage from "../emptyPage/EmptyPage.vue";
import NonLinkedTabs from "../tabs/NonLinkedTabs.vue";
import NonLinkedTab from "../tabs/NonLinkedTab.vue";

export default {
    name: "ProjectTrackingLogModal",

    components: { 
        BaseButton, 
        MainModal, 
        CustomTable,
        DropDownButton,
        UpgradeAccountModal,
        PageSpinner,
        EmptyPage,
        IconSvg,
        NonLinkedTabs,
        NonLinkedTab,
    },

    props: {
        showModal: { type: Boolean, default: false },
        projectId: { type: String, default: "" },
        reload: { type: Boolean, default: false },
    },

    emits: ["cancel"],

    data() {
        return {
            timeSheetHeaders,
            trackerHeaders,
            reactiveShow: this.show || false,
            exportLogsLoading: false,
            showUpgradeModalAlt: false,
            fetchLogsLoading: false,
            fetchLogsLoadingErr: false,
            fetchTrackersLoading: false,
            fetchTrackersLoadingErr: false,
            selectedTab: "Scheduled trackers",
            tabHeaderList: [
                { name: "Scheduled trackers", iconFill: "#212529", iconName: "alarm" },
                { name: "Tracking logs", iconFill: "#212529", iconName: "spreadsheets" },
            ],

        };
    },

    computed: {
        activityLogs() {
            return this.$store.getters["project/timeLogs"].list?.map(item => {
                const elapsedTime = (item.endTime || new Date().getTime()) - item.startTime || 0;
                const seconds = Math.floor((elapsedTime / 1000) % 60);
                const minutes = Math.floor((elapsedTime / (1000 * 60)) % 60);
                const hours = Math.floor(elapsedTime / (1000 * 60 * 60));
            
                const duration = [hours, minutes, seconds]
                    .map(n => n.toString().padStart(2, "0"))
                    .join(":");

                return {
                    startTime: new Date(item.startTime).toLocaleTimeString(),
                    endTime: !item.endTime ? "-" : new Date(item.endTime).toLocaleTimeString(),
                    startDate: new Date(item.startTime).toDateString(),
                    endDate: !item.endTime ? "-" : new Date(item.endTime).toDateString(),
                    duration: duration,
                };
            })?.slice(0, 5);
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        trackers() {
            return this.$store.getters["project/trackers"] || [];
        },
    },

    watch: {
        show(newVal) {
            this.reactiveShow = newVal;
        },
    },

    async mounted() {
        if (!this.trackers?.length) {
            await this.handleFetchTrackers();
        } 
        
        const logs = this.$store.getters["project/timeLogs"].list;

        if (!logs.length) {
            await this.handleFetchLogs();
        }
    },

    methods: {
        closeModal(){
            this.$emit("cancel");
        },

        formatLogTimestamp(timestamp) {
            return new Date(timestamp).toLocaleTimeString();
        },

        async handleFetchTrackers() {            
            try {                      
                this.fetchTrackersLoadingErr = false;   
                this.fetchTrackersLoading = true;   

                const projectId = this.$route.params.id || this.projectId;                
                await this.$store.dispatch("project/getTrackers", { projectId });
            } catch (error) {
                this.fetchTrackersLoadingErr = true;
            } finally {
                this.fetchTrackersLoading = false;

            }
        },

        async exportLogs(num) {
            try {      
                const isPlanValid = this.accountPlan !== "plus";

                // only for plus account users
                if (!isPlanValid) {
                    this.showUpgradeModalAlt = true;
                    return;
                }

                // download code here
                this.exportLogsLoading = true;

                const projectId = this.$route.params.id || this.projectId;
                const resp = await this.$store.dispatch("project/fetchLogs", { 
                    params: { page: 1, limit: num || 50, download: true },
                    projectId,
                    config: {
                        responseType: "blob", // Important for handling binary data,
                        headers: {
                            "Accept": "text/csv",
                        },
                    },
                    download: true,
                });
                
                // Set file name and MIME type
                const url = window.URL.createObjectURL(new Blob([resp], { type: "text/csv" }));
                const link = document.createElement("a");
    
                link.href = url;
                // link.setAttribute("download"); // or the filename you want
                    
                document.body.appendChild(link);
                link.click();
    
                URL.revokeObjectURL(url);
                document.body.removeChild(link);
                
                this.$toast.success("Download complete");
                this.exportLogsLoading = false;
            } catch (error) {
                this.exportLogsLoading = false;
                const msg = error?.message;
                this.$toast.error(msg);
            }
        },

        async handleFetchLogs() {            
            try {                      
                this.fetchLogsLoadingErr = false;     
                this.fetchLogsLoading = true; 

                const projectId = this.$route.params.id || this.projectId;                
                await this.$store.dispatch("project/fetchLogs", { projectId });
                this.fetchLogsLoading = false;      
            } catch (error) {
                this.fetchLogsLoading = false;
                this.fetchLogsLoadingErr = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.modal {
    &--overlay {
        position: fixed; 
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        width: 100%; 
        height: 100%; 
        background-color: rgb(0,0,0); /* Fallback color */
        background-color: rgba(0,0,0,0.4); /* Black w/ opacity */
        animation-timing-function: cubic-bezier(.4,0,.2,1);
        background-color: rgba(0,0,0,.5);
        display: flex;
        isolation: isolate;
        justify-content: center;
        overflow: hidden;
        transition: background-color .5s;
        z-index: 1000;
    }

    &--header {
        display: flex;
        position: relative;
        justify-content: space-between;
        box-shadow: inset 0 -1px #ebeef1;
        padding: 10px 15px;
    }

    &--content {
        background-color: #fefefe;
        margin: 5rem auto;
        border: 1px solid #888;
        width: 80%;
        border-radius: 10px;
        min-height: 566px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        span::after {
            font-size: 10px;
            position: absolute;
            top: 10rem;
            left: 1rem;
            font-family: monospace;
            content: attr(data-state);
            opacity: 0.2;
        }

        &--inner {
            min-height: 310px;
        }
    }

    &--body {
        align-items: center;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 32px;
        padding: 20px;
        width: 650px !important;

        @media screen and (max-width: 800px) {
            width: 500px;
        }
    }

    &--close {
        color: #aaaaaa;
        font-size: 28px;
        outline: none;
        text-align: center;
        border: none;
        background: transparent;
        cursor: pointer;
        font-weight: 600;

        &:hover, &:focus {
            color: #000;
        }
    }
}

.wrap {
    min-height: 405px;
}
</style>